// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/index.js

import React, { useState, useEffect } from 'react';
import { FiChevronRight as ChevronRight } from 'react-icons/fi';
import Header from './components/Header';
import Hero from './components/Hero';
import Programs from './components/Programs';
import Gallery from './components/Gallery';
import Faculty from './components/Faculty';
import FacultyProfileModal from './components/FacultyProfileModal';
import Footer from './components/Footer';
import Modals from './components/Modals';
import Announcements from './components/Announcements';
import ImageModal from './components/ImageModal';

// New Section Imports
import Statistics from './sections/Statistics';
import Accreditations from './sections/Accreditations';
import Infrastructure from './sections/Infrastructure';
import Testimonials from './sections/Testimonials';
import Collaborations from './sections/Collaborations';
import Awards from './sections/Awards';
import Leadership from './sections/Leadership';
import Recruiters from './sections/Recruiters';
import Events from './sections/Events';
import News from './sections/News';
import FAQ from './sections/FAQ';

// ──────────────────────────────────────────────────────────────
// Context for branding colors
// Declared at module scope — created ONCE, reference never changes.
// ──────────────────────────────────────────────────────────────
export const BrandingContext = React.createContext({});

// ──────────────────────────────────────────────────────────────
// BrandingProvider — declared OUTSIDE TenantWebsite.
// ──────────────────────────────────────────────────────────────
const BrandingProvider = ({ brandColors, children }) => (
  <BrandingContext.Provider value={brandColors}>
    {children}
  </BrandingContext.Provider>
);

// Helper to ensure colors and fonts are always available
const getBrandColors = (branding) => {
  const primaryColor = branding?.primaryColor || '#1a1a2e';
  const secondaryColor = branding?.secondaryColor || '#c9a84c';
  const fontFamily = branding?.fontFamily || 'Inter';

  return {
    primary: primaryColor,
    secondary: secondaryColor,
    fontFamily: fontFamily,
    fontHeading: branding?.fontHeading || fontFamily,
    fontBody: branding?.fontBody || fontFamily,
    fontMono: branding?.fontMono || 'monospace',
    primaryLight: `${primaryColor}20`,
    secondaryLight: `${secondaryColor}20`,
    primaryDark: `${primaryColor}80`,
    secondaryDark: `${secondaryColor}80`,
  };
};

const TenantWebsite = (props) => {
  const [selectedFaculty, setSelectedFaculty] = useState(null);
  const [, setFontLoaded] = useState(false);
  const [, setFontsFixed] = useState(false);

  const {
    websiteData,
    subdomain,
    handleSignIn,
    email,
    setEmail,
    password,
    setPassword,
    loginError,
  } = props;

  // Normalize data - handle both old and new structure
  const website = websiteData?.website || websiteData || {};

  // Extract all sections directly from website
  const branding = website?.branding || {};
  const hero = website?.hero || {};
  const statistics = website?.statistics || {};
  const accreditations = website?.accreditations || {};
  const programs = website?.programs || {};
  const infrastructure = website?.infrastructure || {};
  const facultyData = website?.faculty || {};
  const testimonials = website?.testimonials || {};
  const gallery = website?.gallery || {};
  const collaborations = website?.collaborations || {};
  const awards = website?.awards || {};
  const leadership = website?.leadership || {};
  const recruiters = website?.recruiters || {};
  const events = website?.events || {};
  const news = website?.news || {};
  const faq = website?.faq || {};
  const footer = website?.footer || {};
  const content = website?.content || {};

  const apiBaseURL = (website && website.apiBaseURL) || '';

  // Extract announcements from content
  const announcements = content?.announcements || {};

  // Get brand colors with fallbacks
  const brandColors = getBrandColors(branding);

  // ──────────────────────────────────────────────────────────────
  // FORCE FONT LOAD - BYPASSES BROWSER CACHE
  // ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const forceLoadFont = async () => {
      const fontFamily = brandColors.fontFamily;

      if (!fontFamily) {
        console.warn('⚠️ No font family specified, skipping force load');
        return;
      }

      // Step 1: Check if font is already rendering properly
      const testEl = document.createElement('div');
      testEl.style.fontFamily = `"${fontFamily}", serif`;
      testEl.style.position = 'absolute';
      testEl.style.left = '-9999px';
      testEl.style.top = '-9999px';
      testEl.style.fontSize = '100px';
      testEl.textContent = 'Test';
      document.body.appendChild(testEl);

      const computedFont = getComputedStyle(testEl).fontFamily;
      document.body.removeChild(testEl);

      // Check if font is actually rendering
      const isRendering = computedFont.includes(fontFamily);

      // Step 2: Check if font is loaded in document.fonts
      const isLoaded = document.fonts.check(`1em "${fontFamily}"`);

      // Step 3: If font is not rendering or not loaded, force load it
      if (!isRendering || !isLoaded) {
        // Remove any existing force load links
        const existingLinks = document.querySelectorAll('#font-force-load, #google-fonts-reload');
        existingLinks.forEach((link) => link.remove());

        // Remove existing Google Fonts links to prevent conflicts
        const googleFontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
        googleFontLinks.forEach((link) => {
          if (!link.id || (link.id !== 'font-force-load' && link.id !== 'google-fonts-reload')) {
            link.remove();
          }
        });

        // Generate unique cache buster
        const cacheBuster = Date.now() + '_' + Math.random().toString(36).substring(2, 8);

        // Create fresh link with cache busting
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(/ /g, '+')}:wght@300;400;500;600;700;800;900&display=swap&v=${cacheBuster}`;
        link.id = 'font-force-load';
        link.crossOrigin = 'anonymous';

        // Append to head
        document.head.appendChild(link);

        // Step 4: Wait for font to load and apply it
        await new Promise((resolve) => {
          link.onload = function () { resolve(); };
          link.onerror = function () { resolve(); };
          setTimeout(() => { resolve(); }, 5000);
        });

        await new Promise((resolve) => setTimeout(resolve, 500));

        // Step 6: Force apply the font to ALL elements with !important
        const styleEl = document.createElement('style');
        styleEl.id = 'font-force-apply';
        styleEl.textContent = `
          * { font-family: '${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }
          h1, h2, h3, h4, h5, h6 { font-family: '${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }
          p, span, div, li, a, button { font-family: '${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }
          .section-title, .section-subtitle { font-family: '${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }
        `;
        document.head.appendChild(styleEl);

        document.querySelectorAll('#hero, #hero *, main, main *').forEach((el) => {
          el.style.fontFamily = `'${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important`;
        });

        const heroSection = document.getElementById('hero');
        if (heroSection) {
          heroSection.style.fontFamily = `'${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important`;
        }

        document.querySelectorAll('#hero h1, #hero h2, #hero h3, #hero p, #hero span, #hero div, #hero button').forEach((el) => {
          el.style.fontFamily = `'${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important`;
        });

        document.querySelectorAll('header, header *, .header, .header *').forEach((el) => {
          el.style.fontFamily = `'${fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important`;
        });

        setFontsFixed(true);
        setFontLoaded(true);
      } else {
        setFontLoaded(true);
      }
    };

    const timer = setTimeout(() => { forceLoadFont(); }, 300);
    return () => clearTimeout(timer);
  }, [brandColors.fontFamily]);

  // Build nav items dynamically from sections that have content
  const buildNavItems = () => {
    const items = [];
    items.push({ id: 'hero', label: 'Home' });

    if (announcements?.primary || announcements?.secondary?.length > 0) {
      items.push({ id: 'announcements', label: 'Announcements' });
    }
    if (statistics?.items?.length > 0 && statistics?.isActive !== false) {
      items.push({ id: 'statistics', label: statistics?.sectionTitle || 'Statistics' });
    }
    if (accreditations?.items?.length > 0 && accreditations?.isActive !== false) {
      items.push({ id: 'accreditations', label: accreditations?.sectionTitle || 'Accreditations' });
    }
    if (programs?.items?.length > 0) {
      items.push({ id: 'programs', label: programs?.sectionTitle || 'Programs' });
    }
    if (infrastructure?.items?.length > 0 && infrastructure?.isActive !== false) {
      items.push({ id: 'infrastructure', label: infrastructure?.sectionTitle || 'Infrastructure' });
    }
    if (facultyData?.items?.length > 0) {
      items.push({ id: 'faculty', label: facultyData?.sectionTitle || 'Faculty' });
    }
    if (testimonials?.items?.length > 0 && testimonials?.isActive !== false) {
      items.push({ id: 'testimonials', label: testimonials?.sectionTitle || 'Testimonials' });
    }
    if (gallery?.items?.length > 0) {
      items.push({ id: 'gallery', label: gallery?.sectionTitle || 'Gallery' });
    }
    if (collaborations?.items?.length > 0 && collaborations?.isActive !== false) {
      items.push({ id: 'collaborations', label: collaborations?.sectionTitle || 'Collaborations' });
    }
    if (awards?.items?.length > 0 && awards?.isActive !== false) {
      items.push({ id: 'awards', label: awards?.sectionTitle || 'Awards' });
    }
    if (leadership?.items?.length > 0 && leadership?.isActive !== false) {
      items.push({ id: 'leadership', label: leadership?.sectionTitle || 'Leadership' });
    }
    if (recruiters?.items?.length > 0) {
      items.push({ id: 'recruiters', label: recruiters?.sectionTitle || 'Recruiters' });
    }
    if (events?.items?.length > 0 && events?.isActive !== false) {
      items.push({ id: 'events', label: events?.sectionTitle || 'Events' });
    }
    if (news?.items?.length > 0 && news?.isActive !== false) {
      items.push({ id: 'news', label: news?.sectionTitle || 'News' });
    }
    if (faq?.items?.length > 0 && faq?.isActive !== false) {
      items.push({ id: 'faq', label: faq?.sectionTitle || 'FAQ' });
    }

    return items;
  };

  const navItems = buildNavItems();
  const [activeSection, setActiveSection] = useState('hero');

  const handleNavClick = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const headerOffset = 88;
      const elementPosition = el.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: Math.max(0, elementPosition - headerOffset), behavior: 'smooth' });
    }
    setActiveSection(id);
  };

  const [selectedImageIndex, setSelectedImageIndex] = useState(-1);
  const [showImageModal, setShowImageModal] = useState(false);

  // Faculty data extraction
  const facultyList = facultyData?.items || [];

  const groupByDept = (list = []) => {
    const groups = {};
    list.forEach((m) => {
      const dept = (m.department || 'General').trim() || 'General';
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(m);
    });
    Object.keys(groups).forEach((d) => {
      groups[d].sort((a, b) => (a.order || 9999) - (b.order || 9999));
    });
    return groups;
  };

  const facultyByDepartment = groupByDept(facultyList);

  const shouldRenderSection = (section, sectionName) => {
    if (!section) return false;
    const ignoreActiveCheck = ['programs', 'faculty', 'gallery', 'recruiters'];
    if (ignoreActiveCheck.includes(sectionName)) {
      return section.items && section.items.length > 0;
    }
    if (section.isActive === false) return false;
    if (section.items && section.items.length === 0) return false;
    return true;
  };

  return (
    <BrandingProvider brandColors={brandColors}>
      <div
        className="min-h-screen bg-white antialiased"
        style={{
          fontFamily: `'${brandColors.fontFamily}', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`,
        }}
      >
        <Header
          branding={branding}
          subdomain={subdomain}
          onLogin={() => {}}
          handleSignIn={handleSignIn}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          loginError={loginError}
          navItems={navItems}
          activeSection={activeSection}
          onNavClick={handleNavClick}
          apiBaseURL={apiBaseURL}
          brandColors={brandColors}
        />

        <main className="pt-20">
          {/* Hero Section */}
          <Hero
            hero={hero}
            branding={branding}
            brandColors={brandColors}
            onPrimaryAction={() => { setEmail && setEmail(''); }}
          />

          {/* Redesigned & Repositioned Marquee Announcements Section */}
          {(announcements?.primary || announcements?.secondary?.length > 0) && (
            <Announcements announcements={announcements} brandColors={brandColors} />
          )}

          {/* Statistics Section */}
          {shouldRenderSection(statistics, 'statistics') && (
            <Statistics statistics={statistics} brandColors={brandColors} />
          )}

          {/* Accreditations Section */}
          {shouldRenderSection(accreditations, 'accreditations') && (
            <Accreditations accreditations={accreditations} brandColors={brandColors} />
          )}

          {/* Programs Section */}
          {shouldRenderSection(programs, 'programs') && (
            <Programs
              programs={programs.items || []}
              sectionTitle={programs.sectionTitle}
              sectionSubtitle={programs.sectionSubtitle}
              displayStyle={programs.displayStyle}
              brandColors={brandColors}
              onViewProgram={() => {}}
              apiBaseURL={apiBaseURL}
            />
          )}

          {/* Infrastructure Section */}
          {shouldRenderSection(infrastructure, 'infrastructure') && (
            <Infrastructure infrastructure={infrastructure} brandColors={brandColors} />
          )}

          {/* Faculty Section */}
          {shouldRenderSection(facultyData, 'faculty') && (
            <Faculty
              facultyByDepartment={facultyByDepartment}
              facultyList={facultyList}
              onViewProfile={(member) => setSelectedFaculty(member)}
              apiBaseURL={apiBaseURL}
              sectionTitle={facultyData.sectionTitle}
              sectionSubtitle={facultyData.sectionSubtitle}
              brandColors={brandColors}
            />
          )}

          {/* Testimonials Section */}
          {shouldRenderSection(testimonials, 'testimonials') && (
            <Testimonials
              testimonials={testimonials}
              apiBaseURL={apiBaseURL}
            />
          )}

          {/* Gallery Section */}
          {shouldRenderSection(gallery, 'gallery') && (
            <Gallery
              gallery={gallery}
              apiBaseURL={apiBaseURL}
              maxItems={8}
              brandColors={brandColors}
              onOpenImage={(idx) => {
                setSelectedImageIndex(idx);
                setShowImageModal(true);
              }}
            />
          )}

          {/* Collaborations Section */}
          {shouldRenderSection(collaborations, 'collaborations') && (
            <Collaborations collaborations={collaborations} brandColors={brandColors} />
          )}

          {/* Awards Section */}
          {shouldRenderSection(awards, 'awards') && (
            <Awards awards={awards} brandColors={brandColors} />
          )}

          {/* Leadership Section */}
          {shouldRenderSection(leadership, 'leadership') && (
            <Leadership leadership={leadership} brandColors={brandColors} />
          )}

          {/* Recruiters Section */}
          {shouldRenderSection(recruiters, 'recruiters') && (
            <Recruiters recruiters={recruiters} brandColors={brandColors} />
          )}

          {/* Events Section */}
          {shouldRenderSection(events, 'events') && (
            <Events events={events} brandColors={brandColors} />
          )}

          {/* News Section */}
          {shouldRenderSection(news, 'news') && (
            <News news={news} brandColors={brandColors} />
          )}

          {/* FAQ Section */}
          {shouldRenderSection(faq, 'faq') && (
            <FAQ faq={faq} brandColors={brandColors} />
          )}

          {/* CTA Section */}
          <section className="py-20 md:py-28 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-95"
              style={{
                background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryDark} 100%)`
              }}
            />
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                <defs>
                  <pattern id="cta-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                    <path d="M 8 0 L 0 0 0 8" fill="none" stroke="white" strokeWidth="0.5" />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#cta-grid)" />
              </svg>
            </div>
            <div className="container mx-auto px-6 lg:px-12 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h2
                  className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4"
                  style={{ fontFamily: `'${brandColors.fontHeading}', '${brandColors.fontFamily}', sans-serif` }}
                >
                  Ready to Start Your Journey?
                </h2>
                <p
                  className="text-lg text-white/60 font-light mb-10 max-w-2xl mx-auto"
                  style={{ fontFamily: `'${brandColors.fontBody}', '${brandColors.fontFamily}', sans-serif` }}
                >
                  Join us and become part of our vibrant learning community
                </p>
                <button
                  onClick={() => window.location.href = '/'}
                  className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-medium text-base transition-all duration-500 hover:shadow-2xl hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: brandColors.secondary,
                    color: '#ffffff',
                    fontFamily: `'${brandColors.fontBody}', '${brandColors.fontFamily}', sans-serif`
                  }}
                >
                  <span>Get Started Today</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </section>
        </main>

        {footer && (
          <Footer
            footer={footer}
            branding={branding}
            navItems={navItems}
            apiBaseURL={apiBaseURL}
            brandColors={brandColors}
          />
        )}

        <Modals
          handleSignIn={handleSignIn}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          loginError={loginError}
        />

        {selectedFaculty && (
          <FacultyProfileModal
            member={selectedFaculty}
            onClose={() => setSelectedFaculty(null)}
            apiBaseURL={apiBaseURL}
            brandColors={brandColors}
          />
        )}

        {showImageModal && (
          <ImageModal
            images={gallery?.items || []}
            currentIndex={selectedImageIndex}
            onClose={() => {
              setShowImageModal(false);
              setSelectedImageIndex(-1);
            }}
            onNavigate={(newIndex) => {
              if (!Array.isArray(gallery?.items)) return;
              const clamped = Math.max(0, Math.min(gallery.items.length - 1, newIndex));
              setSelectedImageIndex(clamped);
            }}
            apiBaseURL={apiBaseURL}
          />
        )}
      </div>
    </BrandingProvider>
  );
};

export default TenantWebsite;